import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { BehaviorSubject } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import * as Sentry from "@sentry/angular";

import {
  ApruvRateService,
  BillingAccessService,
  hasStoredImpersonationSession,
  shouldBlockImpersonationFallback,
} from '@apruv-client/services';
import { LayoutService } from '../layout/service/layout-service.service';

type BootstrapState = 'idle' | 'running' | 'success' | 'error';

@Injectable({
  providedIn: 'root'
})
export class AuthBootstrapService {
  private bootstrapPromise: Promise<void> | null = null;
  private bootstrapStateSubject = new BehaviorSubject<BootstrapState>('idle');
  public readonly bootstrapState$ = this.bootstrapStateSubject.asObservable();
  private hasBootstrappedOnce = false; // Prevent redirect loops

  constructor(
    private readonly layoutService: LayoutService,
    private readonly keycloak: KeycloakService,
    private readonly router: Router,
    private readonly rateService: ApruvRateService,
    private readonly billingAccessService: BillingAccessService
  ) {}

  /**
   * Run the heavy data bootstrap outside APP_INITIALIZER so Angular can render quickly.
   * Subsequent calls while a run is in progress return the same promise.
   */
  bootstrap(trigger: string = 'unknown'): Promise<void> {
    // If already bootstrapped successfully, return immediately
    if (this.hasBootstrappedOnce && this.bootstrapStateSubject.getValue() === 'success') {
      return Promise.resolve();
    }

    if (this.bootstrapPromise) {
      return this.bootstrapPromise;
    }

    this.bootstrapStateSubject.next('running');
    this.bootstrapPromise = this.runBootstrap(trigger)
      .then(() => {
        this.bootstrapStateSubject.next('success');
        this.hasBootstrappedOnce = true;
      })
      .catch((error) => {
        this.bootstrapStateSubject.next('error');
        console.error('Auth bootstrap error:', error);
        // Wrap HttpErrorResponse in a proper Error for Sentry
        const errorMessage = error?.message || error?.statusText || 'Unknown error';
        const sentryError = error instanceof Error ? error : new Error(`Auth bootstrap failed: ${errorMessage}`);
        Sentry.captureException(sentryError, {
          tags: { component: 'auth-bootstrap' },
          extra: {
            trigger,
            status: error?.status,
            statusText: error?.statusText,
            url: error?.url
          }
        });
      })
      .finally(() => {
        this.bootstrapPromise = null;
      });

    return this.bootstrapPromise;
  }

  private async runBootstrap(trigger: string): Promise<void> {
    const path = window.location.pathname;
    const url = window.location.href;

    if (shouldBlockImpersonationFallback()) {
      if (!path.startsWith('/access-denied')) {
        await this.router.navigateByUrl('/access-denied?reason=impersonation-ended');
      }
      this.bootstrapStateSubject.next('idle');
      return;
    }

    // Skip bootstrap for public routes that bypass the main portal
    // Must match public routes in app.config.ts and roleGuard.service.ts
    if (path.startsWith('/v1/verify/verification') ||
        path.startsWith('/v2/verify/verification') ||
        path.startsWith('/registration') ||
        path.startsWith('/review') ||
        path.startsWith('/access-denied') ||
        url.includes('verifyLink=true')) {
      Sentry.addBreadcrumb({
        category: 'auth-bootstrap',
        message: 'Skipping bootstrap for public route',
        level: 'info',
        data: { trigger, path, url }
      });
      return;
    }

    const isLoggedIn = await this.keycloak.isLoggedIn();
    if (!isLoggedIn) {
      this.bootstrapStateSubject.next('idle');
      return;
    }

    const token = await this.keycloak.getToken();
    if (!token) {
      throw new Error('AuthBootstrapService: missing Keycloak token');
    }

    const decodedToken: any = jwtDecode(token);
    const billingAccess = await this.billingAccessService.load();
    this.layoutService.permissions = [
      ...billingAccess.permissions,
      ...billingAccess.appRoles,
    ];
    this.layoutService.role = billingAccess.role;
    this.layoutService.userType = billingAccess.userType;
    this.layoutService.superuser =
      this.layoutService.permissions?.includes('super_user') === true;
    this.layoutService.orgs = billingAccess.legacyOrgs;
    this.layoutService.setBillingLocationScope(
      billingAccess.locationsByOrganization,
      billingAccess.locationDetailsByOrganization
    );
    this.layoutService.setBillingCompanyScope(billingAccess.organizations);
    sessionStorage.setItem(
      'locationsByCompany',
      JSON.stringify(billingAccess.locationsByOrganization)
    );

    Sentry.setUser({
      id: decodedToken?.sub || 'unknown',
      username: decodedToken?.preferred_username || 'unknown',
      email: decodedToken?.email || '',
      role: decodedToken?.role || 'unknown'
    });
    Sentry.addBreadcrumb({
      category: 'auth-bootstrap',
      message: 'Loaded billing access and set layout permissions',
      level: 'info',
      data: { trigger }
    });

    const defaultCompanyId = billingAccess.defaultCompanyId;
    const defaultLocationId = billingAccess.defaultLocationId;
    if (defaultCompanyId) {
      this.layoutService.setCompanyId(defaultCompanyId);
    }
    if (defaultLocationId) {
      this.layoutService.setLocationId(defaultLocationId);
    }
    this.layoutService.salesRepDefaultLocations =
      defaultCompanyId && defaultLocationId
        ? [{
            company_id: String(defaultCompanyId),
            location_id: String(defaultLocationId),
          }]
        : undefined;

    if (this.isTechnicianImpersonationRoute(path)) {
      Sentry.addBreadcrumb({
        category: 'auth-bootstrap',
        message: 'Skipping portal bootstrap for technician impersonation route',
        level: 'info',
        data: { trigger, path }
      });
      return;
    }

    await this.layoutService.updateSalesRepDetails();

    if (defaultCompanyId) {
      await this.layoutService
        .fetchInitialData(String(defaultCompanyId), defaultLocationId ? String(defaultLocationId) : undefined)
        .toPromise();
    } else {
      // A billing default controls selection, not whether the user can see
      // their authorized companies. Multi-company users may legitimately have
      // no default, so still populate selectors such as Verify search.
      await this.layoutService.fetchCompanies().toPromise();
    }

    if (this.layoutService.redirectToNewAdminPortalIfNeeded()) {
      return;
    }

    // Resolve which Google Review Reputation screen(s) are exposed for this company so the
    // menu and route guard have the value before routing. Safely defaults to 'v1' on failure.
    try {
      const reputationVersion = await this.rateService
        .getReputationVersion(this.layoutService.getCompanyId())
        .toPromise();
      this.layoutService.setReputationVersion(reputationVersion ?? 'v1');
    } catch (error) {
      console.warn('Auth bootstrap: reputation version lookup failed, defaulting to v1', error);
      this.layoutService.setReputationVersion('v1');
    }

    if (sessionStorage.getItem('redirectAfterLogin')) {
      this.handlePostLoginRedirect();
    } else {
      this.layoutService.redirectUser();
    }
  }

  private handlePostLoginRedirect(): void {
    const savedUrl = sessionStorage.getItem('redirectAfterLogin');
    if (!savedUrl) {
      return;
    }

    sessionStorage.removeItem('redirectAfterLogin');
    this.router.navigateByUrl(savedUrl).catch(err => {
      console.error('Auth bootstrap redirect failed:', err);
      Sentry.captureException(err, {
        tags: { component: 'auth-bootstrap' },
        extra: { savedUrl }
      });
    });
  }

  private isTechnicianImpersonationRoute(path: string): boolean {
    return (
      hasStoredImpersonationSession() &&
      (path.startsWith('/technician') || path.startsWith('/notifications'))
    );
  }
}
